<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/Page-header";
import CheckModal from "@/components/CheckModal";
import DocumentActions from "@/components/document/DocumentActions.vue";
import PhotoEvidence from "@/components/standard/PhotoEvidence";
import eppDeliveryAPI from "@/services/standards/eppDelivery/eppDeliveryAPI";
import newScoresAPI from "@/services/newScores/newScoresAPI";
import Swal from "sweetalert2";
import VersionNote from "@/components/document/VersionNote.vue";

function blankItem() {
  return { elemento: "", marca: "", cantidad: "", estado: "N", fecha_entrega: null, observaciones: "" };
}

export default {
  page: { title: "Acta de Entrega de EPP" },
  components: { VersionNote, Layout, PageHeader, CheckModal, DocumentActions, PhotoEvidence },
  data() {
    return {
      // Texto que queda en el control de versiones del PDF
      versionNote: "",
      title: "4.2.6 Acta de Entrega de EPP",
      items: [
        { text: "Estándares", href: "/forms/estandares" },
        { text: "4.2.6 Entrega de EPP", active: true },
      ],
      companyID: this.$store.getters["company/getCompany"].id || null,
      deliveryId: this.$route.params.delivery || null,
      employees: [],
      clasifCatalog: {},
      riesgos: ["I", "II", "III", "IV", "V"],
      form: {
        employee_id: null,
        fecha_entrega: new Date().toISOString().substring(0, 10),
        trabajador_nombre: null, trabajador_cedula: null, trabajador_cargo: null,
        trabajador_area: null, trabajador_jefe: null, trabajador_centro: null, riesgo: null,
        observaciones: null, responsable_nombre: null, responsable_cargo: null,
      },
      itemsList: [blankItem()],
      clasificacion: {},
      workflowStatus: "draft",
      signedAt: null,
      submitting: false,
      loading: true,
    };
  },
  computed: {
    isSigned() {
      return this.workflowStatus === "signed";
    },
    eppApi() {
      return {
        pdfUrl: (id) => eppDeliveryAPI.pdfUrl(id),
        sign: (id, payload) => eppDeliveryAPI.sign(id, payload),
      };
    },
  },
  methods: {
    goBack() { this.$router.go(-1); },
    addItem() { if (!this.isSigned) this.itemsList.push(blankItem()); },
    removeItem(idx) {
      if (this.isSigned) return;
      this.itemsList.splice(idx, 1);
      if (!this.itemsList.length) this.itemsList.push(blankItem());
    },
    onEmployeeChange() {
      const emp = this.employees.find((e) => e.id === this.form.employee_id);
      if (!emp) return;
      this.form.trabajador_nombre = emp.full_name || null;
      this.form.trabajador_cedula = emp.identification_number || null;
      this.form.trabajador_cargo = emp.position || null;
      // risk_class suele venir como "I".."V"; si coincide con el catálogo lo usamos.
      const rc = String(emp.risk_class || "").toUpperCase().replace(/[^IV]/g, "");
      if (this.riesgos.includes(rc)) this.form.riesgo = rc;
    },
    load() {
      if (!this.deliveryId) { this.loading = false; return; }
      eppDeliveryAPI.get(this.deliveryId)
        .then((r) => {
          if (!r) { this.loading = false; return; }
          Object.keys(this.form).forEach((k) => {
            if (r[k] !== undefined && r[k] !== null) {
              this.form[k] = k === "fecha_entrega" ? String(r[k]).substring(0, 10) : r[k];
            }
          });
          this.itemsList = (r.items && r.items.length) ? r.items.map((x) => ({ ...blankItem(), ...x })) : [blankItem()];
          this.clasificacion = r.clasificacion && typeof r.clasificacion === "object" ? { ...r.clasificacion } : {};
          this.workflowStatus = r.workflow_status || "draft";
          this.signedAt = r.signed_at || null;
          this.loading = false;
        })
        .catch((e) => { console.log(e); this.loading = false; });
    },
    save() {
      if (this.isSigned) return;
      this.submitting = true;
      const payload = { companies_id: this.companyID, ...this.form, items: this.itemsList, clasificacion: this.clasificacion };
      payload.version_note = this.versionNote;
      const req = this.deliveryId ? eppDeliveryAPI.update(this.deliveryId, payload) : eppDeliveryAPI.save(payload);
      req
        .then((res) => {
          newScoresAPI.calculateScores({ companies_id: this.companyID, item_id: 50, status: "Pendiente" }).catch(() => {});
          Swal.fire({ title: "¡Éxito!", text: "Acta guardada.", icon: "success", timer: 1600, showConfirmButton: false });
          this.submitting = false;
          if (!this.deliveryId && res && res.id) {
            this.deliveryId = res.id;
            this.$router.replace({ path: `/forms/estandares/${this.companyID}/entrega-epp/${res.id}` });
          }
        })
        .catch((e) => { console.log(e); this.submitting = false; Swal.fire("Error", "No se pudo guardar el acta", "error"); });
    },
    onSigned(payload) {
      this.workflowStatus = payload.workflow_status;
      this.signedAt = payload.signed_at;
    },
  },
  mounted() {
    eppDeliveryAPI.meta().then((m) => { if (m && m.clasificacion) this.clasifCatalog = m.clasificacion; });
    eppDeliveryAPI.employees(this.companyID).then((r) => { this.employees = (r && r.data) ? r.data : []; });
    this.load();
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-lg-9" v-if="!loading">
        <div class="card">
          <div class="card-body">
            <CheckModal>
              <p class="text-justify">
                Registra la entrega de EPP a un colaborador: selecciónalo para autollenar sus datos, agrega los elementos
                entregados (marca, cantidad, estado y fecha), clasifícalos por tipo de protección y deja la declaración
                de compromiso. La plataforma genera el acta para la firma del responsable de entrega; el trabajador firma
                sobre el documento impreso.
              </p>
            </CheckModal>

            <div v-if="isSigned" class="alert alert-success py-2 my-2 small">
              🔒 Esta acta ya fue firmada; los datos quedan bloqueados para edición.
            </div>

            <form @submit.prevent="save">
              <!-- Datos del trabajador -->
              <h5 class="pt-2">Datos del trabajador</h5>
              <div class="form-row">
                <b-form-group class="col-md-6 px-2" label="Colaborador">
                  <b-form-select v-model="form.employee_id" :disabled="isSigned" @change="onEmployeeChange">
                    <b-form-select-option :value="null">Seleccione un colaborador...</b-form-select-option>
                    <b-form-select-option v-for="e in employees" :key="e.id" :value="e.id">
                      {{ e.full_name }} — {{ e.identification_number }}
                    </b-form-select-option>
                  </b-form-select>
                </b-form-group>
                <b-form-group class="col-md-6 px-2" label="Fecha de entrega">
                  <b-form-input type="date" v-model="form.fecha_entrega" :disabled="isSigned" required />
                </b-form-group>
                <b-form-group class="col-md-6 px-2" label="Nombre completo">
                  <b-form-input v-model="form.trabajador_nombre" :disabled="isSigned" size="sm" />
                </b-form-group>
                <b-form-group class="col-md-3 px-2" label="Identificación">
                  <b-form-input v-model="form.trabajador_cedula" :disabled="isSigned" size="sm" />
                </b-form-group>
                <b-form-group class="col-md-3 px-2" label="Nivel de riesgo">
                  <b-form-select v-model="form.riesgo" :disabled="isSigned" size="sm">
                    <b-form-select-option :value="null">—</b-form-select-option>
                    <b-form-select-option v-for="r in riesgos" :key="r" :value="r">Riesgo {{ r }}</b-form-select-option>
                  </b-form-select>
                </b-form-group>
                <b-form-group class="col-md-3 px-2" label="Cargo / Puesto">
                  <b-form-input v-model="form.trabajador_cargo" :disabled="isSigned" size="sm" />
                </b-form-group>
                <b-form-group class="col-md-3 px-2" label="Área / Proceso">
                  <b-form-input v-model="form.trabajador_area" :disabled="isSigned" size="sm" />
                </b-form-group>
                <b-form-group class="col-md-3 px-2" label="Jefe inmediato">
                  <b-form-input v-model="form.trabajador_jefe" :disabled="isSigned" size="sm" />
                </b-form-group>
                <b-form-group class="col-md-3 px-2" label="Centro de trabajo">
                  <b-form-input v-model="form.trabajador_centro" :disabled="isSigned" size="sm" />
                </b-form-group>
              </div>

              <!-- EPP entregados -->
              <hr />
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">Elementos de protección personal entregados</h5>
                <button type="button" class="btn btn-outline-primary btn-sm" :disabled="isSigned" @click="addItem">
                  <i class="mdi mdi-plus"></i> Agregar
                </button>
              </div>
              <div v-for="(it, idx) in itemsList" :key="idx" class="form-row align-items-end mt-1">
                <b-form-group class="col-md-3 px-2" :label="idx === 0 ? 'Elemento' : ''">
                  <b-form-input v-model="it.elemento" :disabled="isSigned" size="sm" />
                </b-form-group>
                <b-form-group class="col-md-2 px-2" :label="idx === 0 ? 'Marca / Ref.' : ''">
                  <b-form-input v-model="it.marca" :disabled="isSigned" size="sm" />
                </b-form-group>
                <b-form-group class="col-md-1 px-2" :label="idx === 0 ? 'Cant.' : ''">
                  <b-form-input type="number" min="0" v-model="it.cantidad" :disabled="isSigned" size="sm" />
                </b-form-group>
                <b-form-group class="col-md-2 px-2" :label="idx === 0 ? 'Estado' : ''">
                  <b-form-select v-model="it.estado" :disabled="isSigned" size="sm">
                    <b-form-select-option value="N">Nuevo (N)</b-form-select-option>
                    <b-form-select-option value="R">Reposición (R)</b-form-select-option>
                  </b-form-select>
                </b-form-group>
                <b-form-group class="col-md-2 px-2" :label="idx === 0 ? 'Fecha' : ''">
                  <b-form-input type="date" v-model="it.fecha_entrega" :disabled="isSigned" size="sm" />
                </b-form-group>
                <b-form-group class="col-md-1 px-2" :label="idx === 0 ? 'Obs.' : ''">
                  <b-form-input v-model="it.observaciones" :disabled="isSigned" size="sm" />
                </b-form-group>
                <div class="col-md-1 px-2 pb-2">
                  <button type="button" class="btn btn-outline-danger btn-sm py-0" :disabled="isSigned" @click="removeItem(idx)">
                    <i class="mdi mdi-trash-can"></i>
                  </button>
                </div>
              </div>
              <p class="text-muted small mt-1">Estado: N = Nuevo / R = Reposición.</p>

              <!-- Clasificación por tipo de protección -->
              <hr />
              <h5>Clasificación del EPP por tipo de protección <small class="text-muted">(opcional)</small></h5>
              <div class="form-row">
                <b-form-group v-for="(label, key) in clasifCatalog" :key="key" class="col-md-6 px-2" :label="label">
                  <b-form-input v-model="clasificacion[key]" :disabled="isSigned" size="sm" placeholder="Elemento entregado..." />
                </b-form-group>
              </div>

              <b-form-group label="Observaciones">
                <b-form-textarea v-model="form.observaciones" :disabled="isSigned" rows="2" />
              </b-form-group>

              <!-- Responsable de entrega -->
              <hr />
              <h5>Responsable de entrega</h5>
              <div class="form-row">
                <b-form-group class="col-md-6 px-2" label="Nombre">
                  <b-form-input v-model="form.responsable_nombre" :disabled="isSigned" size="sm" />
                </b-form-group>
                <b-form-group class="col-md-6 px-2" label="Cargo">
                  <b-form-input v-model="form.responsable_cargo" :disabled="isSigned" size="sm" />
                </b-form-group>
              </div>

              <VersionNote v-model="versionNote" :editing="!!deliveryId" />

              <div class="text-right mt-2">
                <button type="submit" :disabled="isSigned || submitting" class="btn btn-primary mx-1">Guardar</button>
                <button type="button" class="btn btn-secondary mx-1" @click="goBack">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div class="col-lg-3" v-if="!loading && deliveryId">
        <h6 class="text-muted small mb-1">Acta de Entrega de EPP</h6>
        <DocumentActions
          :document-id="Number(deliveryId)"
          :company-id="companyID"
          :workflow-status="workflowStatus"
          :signed-at="signedAt"
          :api-service="eppApi"
          document-title="Acta de Entrega de EPP"
          signer-role="gerente"
          @signed="onSigned"
        />
      </div>

      <!-- Evidencia física del elemento entregado (se incrusta como anexo en el PDF) -->
      <div class="col-12" v-if="!loading && deliveryId">
        <PhotoEvidence
          :company-id="companyID"
          item-code="4.2.6"
          reference-type="epp_delivery"
          :reference-id="deliveryId"
          category="elemento_fisico"
          :disabled="isSigned"
          title="Evidencia fotográfica del elemento entregado"
          help-text="Fotos del EPP entregado y/o del trabajador con el elemento. Se incluyen como anexo en el PDF firmado."
        />
      </div>

      <div v-if="loading" class="col-12 text-center"><b-spinner variant="primary" label="Loading..."></b-spinner></div>
    </div>
  </Layout>
</template>
